import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Camera,
  Upload,
  ArrowLeft,
  ArrowRight,
  Loader2,
  Image as ImageIcon,
  Sparkles,
  RotateCcw,
  Crop,
  Video,
  VideoOff,
  ThumbsUp,
  ThumbsDown,
  SkipForward,
  History,
  UserCheck,
  RefreshCw,
  WifiOff,
  Gauge,
  ScanText,
  ChevronDown,
  Maximize2,
} from "lucide-react";

const STEP_STATUS_BORDER = {
  pass: "bg-emerald-500/10 border-emerald-500/20",
  fail: "bg-red-500/10 border-red-500/20",
  analyzing: "bg-blue-500/10 border-blue-500/20",
  skipped: "bg-slate-500/10 border-slate-500/20",
};

const getStatusIcon = (status) => {
  switch (status) {
    case "pass": return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
    case "fail": return <XCircle className="w-4 h-4 text-red-500" />;
    case "warning": return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    case "analyzing": return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
    case "skipped": return <SkipForward className="w-4 h-4 text-slate-500" />;
    default: return <Clock className="w-4 h-4 text-slate-400" />;
  }
};

const cleanSummary = (summary) => {
  if (!summary) return summary;
  const match = summary.match(/Summary:\s*(.+)/s);
  return match ? match[1].trim() : summary;
};

const OcrResultsBreakdown = ({ results, combinedText, screensFound }) => {
  const [open, setOpen] = useState(false);
  const passed  = results.filter(r => r.passed === true).length;
  const failed  = results.filter(r => r.passed === false).length;
  const unclear = results.filter(r => r.passed === null).length;

  const screenBadge = screensFound === null
    ? null
    : screensFound === 0
      ? <span className="text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 px-1.5 py-0.5 rounded font-normal">No screen · full image</span>
      : <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 px-1.5 py-0.5 rounded font-normal">{screensFound} screen{screensFound !== 1 ? "s" : ""}</span>;

  return (
    <div className="mt-3 pt-3 border-t border-current/10">
      <button
        type="button"
        className="flex items-center gap-1.5 text-xs font-medium w-full text-left hover:opacity-80 transition-opacity"
        onClick={() => setOpen(o => !o)}
      >
        <ScanText className="w-3.5 h-3.5 shrink-0" />
        OCR Scan — {passed} passed · {failed} failed{unclear > 0 ? ` · ${unclear} inconclusive` : ""}
        {screenBadge}
        <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-2 space-y-1.5">
          {combinedText && (
            <div className="rounded px-2 py-1.5 bg-muted/40 text-xs">
              <p className="font-medium text-muted-foreground mb-0.5">Extracted text</p>
              <p className="font-mono break-all leading-relaxed">{combinedText}</p>
            </div>
          )}
          {results.map((r, i) => (
            <div key={i} className={`flex items-start gap-2 rounded px-2 py-1.5 text-xs ${
              r.passed === true  ? "bg-emerald-50 dark:bg-emerald-950/30" :
              r.passed === false ? "bg-red-50 dark:bg-red-950/30" :
                                   "bg-muted/40"
            }`}>
              <span className="shrink-0 mt-0.5">
                {r.passed === true  ? "✓" : r.passed === false ? "✗" : "?"}
              </span>
              <div className="flex-1 min-w-0">
                <span className="font-medium">{r.label}</span>
                {r.found != null && (
                  <span className="ml-1.5 font-mono text-muted-foreground">"{r.found}"</span>
                )}
                {r.passed !== true && r.expected && (
                  <p className="text-muted-foreground mt-0.5 opacity-80">
                    Expected: <span className="font-mono">{r.expected}</span>
                  </p>
                )}
                {r.reason && (
                  <p className="text-muted-foreground mt-0.5 opacity-80">{r.reason}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const StepVerificationPanel = ({
  test,
  currentStepResult,
  currentStep,
  activeStep,
  selectedImage,
  setSelectedImage,
  imageQuality,
  notes,
  setNotes,
  verifying,
  cameraActive,
  cameraError,
  videoRef,
  fileInputRef,
  getPhaseLabel,
  getPhaseColor,
  onStartCamera,
  onStopCamera,
  onCapturePhoto,
  onImageUpload,
  onVerifyStep,
  onManualOverride,
  onSkipStep,
  onPrevStep,
  onCropImage,
  onReAnalyze,
  onNextStep,
  totalSteps,
  totalStepResults
}) => {
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [overrideAction, setOverrideAction] = useState(null);
  const [overrideComments, setOverrideComments] = useState("");
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [skipReason, setSkipReason] = useState("");
  const [showRetryHistory, setShowRetryHistory] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(null); // { src, title }

  const handleOverrideSubmit = () => {
    if (!overrideComments.trim()) return;
    onManualOverride(overrideAction, overrideComments);
    setShowOverrideDialog(false);
    setOverrideComments("");
    setOverrideAction(null);
  };

  const handleSkipSubmit = () => {
    if (!skipReason.trim()) return;
    onSkipStep(skipReason);
    setShowSkipDialog(false);
    setSkipReason("");
  };

  const retryHistory = currentStepResult?.retry_history || [];
  const retryCount = currentStepResult?.retry_count || 0;
  const manualOverride = currentStepResult?.manual_override;

  return (
    <>
      <Card className="lg:col-span-2" data-testid="step-verification-panel">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline">
                  Step {activeStep + 1} of {totalSteps}
                </Badge>
                {retryCount > 0 && (
                  <Badge variant="secondary" className="cursor-pointer" onClick={() => setShowRetryHistory(true)}>
                    <History className="w-3 h-3 mr-1" />
                    Attempt {retryCount + 1}
                  </Badge>
                )}
                {manualOverride && (
                  <Badge variant={manualOverride.action === "accept" || manualOverride.action === "skip" ? "default" : "destructive"} className="text-xs">
                    <UserCheck className="w-3 h-3 mr-1" />
                    {manualOverride.action === "skip" ? "Skipped" : `Manual ${manualOverride.action}`}
                  </Badge>
                )}
              </div>
              <CardTitle>{currentStepResult?.step_name}</CardTitle>
              <CardDescription>{currentStep?.description}</CardDescription>
            </div>
            {currentStep && (
              <Badge
                style={{ backgroundColor: getPhaseColor(currentStep.phase, currentStep.phase_color) }}
                className="text-white"
              >
                {getPhaseLabel(currentStep.phase, currentStep.phase_name)}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Current Status */}
          {currentStepResult?.status !== "pending" && (
            <>
              {/* AI error banner — quota exceeded or other AI failure */}
              {currentStepResult.ai_analysis?.error_type && (
                <div className="p-4 rounded-lg border bg-orange-50 border-orange-300 dark:bg-orange-950/30 dark:border-orange-700">
                  <div className="flex items-start gap-3">
                    {currentStepResult.ai_analysis?.error_type === "quota_exceeded"
                      ? <Gauge className="w-5 h-5 text-orange-500 mt-0.5 shrink-0" />
                      : <WifiOff className="w-5 h-5 text-orange-500 mt-0.5 shrink-0" />
                    }
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-orange-700 dark:text-orange-400">
                        {currentStepResult.ai_analysis?.error_type === "quota_exceeded"
                          ? "AI Quota Exceeded"
                          : "AI Analysis Failed"
                        }
                      </p>
                      <p className="text-sm text-orange-600 dark:text-orange-300 mt-0.5">
                        {currentStepResult.ai_analysis?.summary || "An error occurred during AI analysis."}
                      </p>
                      <div className="flex gap-2 mt-3 flex-wrap">
                        {currentStepResult.captured_image_base64 && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-orange-400 text-orange-700 hover:bg-orange-100 dark:text-orange-300 dark:border-orange-600 dark:hover:bg-orange-950"
                            onClick={() => onReAnalyze()}
                            disabled={verifying}
                          >
                            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${verifying ? "animate-spin" : ""}`} />
                            Re-verify with same photo
                          </Button>
                        )}
                        <p className="text-xs text-orange-500 dark:text-orange-400 self-center">
                          or retake the photo below, or use Manual Accept / Reject.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Normal pass/fail/warning/analyzing result */}
              {!currentStepResult.ai_analysis?.error_type && (
                <div className={`p-4 rounded-lg border ${
                  STEP_STATUS_BORDER[currentStepResult.status] || "bg-amber-500/10 border-amber-500/20"
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {getStatusIcon(currentStepResult.status)}
                    <span className="font-semibold capitalize">{currentStepResult.status}</span>
                    {currentStepResult.ai_analysis?.confidence != null && (
                      <Badge variant="outline" className="ml-auto font-mono">
                        {currentStepResult.ai_analysis.confidence}% confidence
                      </Badge>
                    )}
                  </div>
                  {currentStepResult.ai_analysis?.summary && (
                    <p className="text-sm">{cleanSummary(currentStepResult.ai_analysis.summary)}</p>
                  )}
                  {currentStepResult.ai_analysis?.issues?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-medium mb-1">Issues:</p>
                      <ul className="text-xs list-disc list-inside">
                        {currentStepResult.ai_analysis.issues.map((issue, i) => (
                          <li key={`issue-${i}`}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* OCR validation breakdown */}
                  {currentStepResult.ai_analysis?.source === "ocr_validation" &&
                    currentStepResult.ai_analysis?.ocr_results?.length > 0 && (
                    <OcrResultsBreakdown
                      results={currentStepResult.ai_analysis.ocr_results}
                      combinedText={currentStepResult.ai_analysis.combined_text}
                      screensFound={currentStepResult.ai_analysis.screens_found}
                    />
                  )}


                  {currentStepResult.status === "warning" && (
                    <div className="mt-3 pt-3 border-t border-amber-300 dark:border-amber-700 flex items-start gap-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                      <p className="text-xs text-amber-700 dark:text-amber-400">
                        Ambiguous result — AI could not confidently pass or fail this step. Please review the image and use <strong>Manual Accept</strong> or <strong>Manual Reject</strong>.
                      </p>
                    </div>
                  )}
                  {manualOverride && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs text-muted-foreground">
                        <strong>{manualOverride.action === "skip" ? "Skipped" : `Manually ${manualOverride.action}ed`}</strong> by {manualOverride.overridden_by}: {manualOverride.comments}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Manual Override / Skip Buttons — show when step has a result */}
          {currentStepResult?.status !== "pending" && currentStepResult?.status !== "analyzing" && (test.status === "in_progress" || test.status === "passed" || test.status === "failed") && (
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                onClick={() => { setOverrideAction("accept"); setShowOverrideDialog(true); }}
              >
                <ThumbsUp className="w-3.5 h-3.5 mr-1.5" />
                Manual Accept
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-300 hover:bg-red-50"
                onClick={() => { setOverrideAction("reject"); setShowOverrideDialog(true); }}
              >
                <ThumbsDown className="w-3.5 h-3.5 mr-1.5" />
                Manual Reject
              </Button>
              {(test.status === "in_progress" || currentStepResult?.status === "warning") && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-slate-600 border-slate-300 hover:bg-slate-50"
                  onClick={() => setShowSkipDialog(true)}
                >
                  <SkipForward className="w-3.5 h-3.5 mr-1.5" />
                  Skip Step
                </Button>
              )}
              {(currentStepResult?.captured_image_base64 || currentStepResult?.captured_image_url) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-blue-600 border-blue-300 hover:bg-blue-50"
                  onClick={() => onReAnalyze()}
                  disabled={verifying}
                >
                  <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${verifying ? "animate-spin" : ""}`} />
                  Re-analyze
                </Button>
              )}
              {currentStep?.ocr_validation?.enabled &&
                (currentStepResult?.captured_image_base64 || currentStepResult?.captured_image_url) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-purple-600 border-purple-300 hover:bg-purple-50"
                  onClick={() => onReAnalyze(true)}
                  disabled={verifying}
                  title="OCR couldn't read this correctly? Get Gemini's opinion instead."
                >
                  <Sparkles className={`w-3.5 h-3.5 mr-1.5 ${verifying ? "animate-pulse" : ""}`} />
                  Re-analyze with AI
                </Button>
              )}
            </div>
          )}

          {/* Skip button for pending steps */}
          {currentStepResult?.status === "pending" && test.status === "in_progress" && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="text-slate-600"
                onClick={() => setShowSkipDialog(true)}
              >
                <SkipForward className="w-3.5 h-3.5 mr-1.5" />
                Skip This Step
              </Button>
            </div>
          )}

          {/* Image Section */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Reference Image */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Reference Image</label>
              <div className="aspect-video bg-muted rounded-lg border-2 border-dashed flex items-center justify-center overflow-hidden relative">
                {(currentStep?.reference_image_url || currentStep?.reference_image_base64) ? (
                  <>
                    <img
                      src={currentStep.reference_image_url || `data:image/jpeg;base64,${currentStep.reference_image_base64}`}
                      alt="Reference"
                      className="w-full h-full object-contain rounded-lg"
                    />
                    <button
                      onClick={() => setFullscreenImage({ src: currentStep.reference_image_url || `data:image/jpeg;base64,${currentStep.reference_image_base64}`, title: "Reference Image" })}
                      className="absolute top-2 right-2 p-1.5 rounded-md bg-black/50 text-white hover:bg-black/70 transition-colors"
                    >
                      <Maximize2 className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <div className="text-center text-muted-foreground">
                    <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No reference image</p>
                  </div>
                )}
              </div>
            </div>

            {/* Captured/Upload Image or Camera */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {cameraActive ? "Camera Preview" : "Captured Image"}
              </label>
              <div className="aspect-video bg-muted rounded-lg border-2 border-dashed flex items-center justify-center overflow-hidden relative">
                {cameraActive ? (
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : selectedImage ? (
                  <>
                    <img
                      src={`data:image/jpeg;base64,${selectedImage}`}
                      alt="Captured"
                      className="w-full h-full object-contain rounded-lg"
                    />
                    {imageQuality && (
                      <Badge
                        variant={imageQuality.quality === "good" ? "default" : imageQuality.quality === "fair" ? "secondary" : "destructive"}
                        className="absolute top-2 left-2 text-xs shadow-sm"
                      >
                        {imageQuality.quality === "good" ? "Good quality" : imageQuality.quality === "fair" ? "Fair quality" : "Poor quality"}
                      </Badge>
                    )}
                    <button
                      onClick={() => setFullscreenImage({ src: `data:image/jpeg;base64,${selectedImage}`, title: "Captured Image" })}
                      className="absolute top-2 right-2 p-1.5 rounded-md bg-black/50 text-white hover:bg-black/70 transition-colors"
                    >
                      <Maximize2 className="w-4 h-4" />
                    </button>
                  </>
                ) : (currentStepResult?.captured_image_url || currentStepResult?.captured_image_base64) ? (
                  <>
                    <img
                      src={currentStepResult.captured_image_url || `data:image/jpeg;base64,${currentStepResult.captured_image_base64}`}
                      alt="Previous capture"
                      className="w-full h-full object-contain rounded-lg opacity-50"
                    />
                    <button
                      onClick={() => setFullscreenImage({ src: currentStepResult.captured_image_url || `data:image/jpeg;base64,${currentStepResult.captured_image_base64}`, title: "Captured Image" })}
                      className="absolute top-2 right-2 p-1.5 rounded-md bg-black/50 text-white hover:bg-black/70 transition-colors"
                    >
                      <Maximize2 className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <div className="text-center text-muted-foreground">
                    <Camera className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Use camera or upload</p>
                  </div>
                )}
              </div>

              {/* Camera/Upload Controls */}
              <div className="flex gap-2">
                {cameraActive ? (
                  <>
                    <Button onClick={onCapturePhoto} className="flex-1">
                      <Camera className="w-4 h-4 mr-2" />
                      Capture
                    </Button>
                    <Button variant="outline" onClick={onStopCamera}>
                      <VideoOff className="w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" onClick={onStartCamera} className="flex-1">
                      <Video className="w-4 h-4 mr-2" />
                      Camera
                    </Button>
                    <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="flex-1">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload
                    </Button>
                  </>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={onImageUpload}
              />

              {cameraError && (
                <p className="text-xs text-red-500">{cameraError}</p>
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Notes (Optional)</label>
            <Textarea
              placeholder="Add any observations or notes about this step..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="resize-none"
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t">
            <Button variant="outline" onClick={onPrevStep} disabled={activeStep === 0}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>

            <div className="flex gap-2 items-center">
              {imageQuality?.quality === "poor" && selectedImage && (
                <span className="text-xs text-red-500 max-w-[200px] truncate" title={imageQuality.suggestions}>
                  {imageQuality.issues?.[0] || "Poor quality"}
                </span>
              )}
              {selectedImage && (
                <>
                  <Button variant="ghost" size="sm" onClick={onCropImage}>
                    <Crop className="w-4 h-4 mr-1" />
                    Crop
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedImage(null)}>
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                </>
              )}
              <Button
                onClick={onVerifyStep}
                disabled={!selectedImage || verifying}
                className="min-w-[140px]"
              >
                {verifying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {retryCount > 0 ? "Re-verify" : "Verify Step"}
                  </>
                )}
              </Button>
            </div>

            <Button variant="outline" onClick={onNextStep} disabled={activeStep === totalStepResults - 1}>
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Manual Override Dialog */}
      <Dialog open={showOverrideDialog} onOpenChange={setShowOverrideDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {overrideAction === "accept" ? (
                <ThumbsUp className="w-5 h-5 text-emerald-500" />
              ) : (
                <ThumbsDown className="w-5 h-5 text-red-500" />
              )}
              Manual {overrideAction === "accept" ? "Accept" : "Reject"}: {currentStepResult?.step_name}
            </DialogTitle>
            <DialogDescription>
              Provide a reason for {overrideAction === "accept" ? "accepting" : "rejecting"} this step manually.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder={overrideAction === "accept"
              ? "e.g., Verified visually — AI confidence was low due to lighting but assembly is correct"
              : "e.g., Component clearly misaligned despite AI passing"}
            value={overrideComments}
            onChange={(e) => setOverrideComments(e.target.value)}
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOverrideDialog(false)}>Cancel</Button>
            <Button
              variant={overrideAction === "accept" ? "default" : "destructive"}
              disabled={!overrideComments.trim()}
              onClick={handleOverrideSubmit}
            >
              {overrideAction === "accept" ? "Accept Step" : "Reject Step"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Skip Dialog */}
      <Dialog open={showSkipDialog} onOpenChange={setShowSkipDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SkipForward className="w-5 h-5 text-slate-500" />
              Skip Step: {currentStepResult?.step_name}
            </DialogTitle>
            <DialogDescription>
              Provide a reason why this step is not applicable.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="e.g., No flow meter installed in this configuration"
            value={skipReason}
            onChange={(e) => setSkipReason(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSkipDialog(false)}>Cancel</Button>
            <Button disabled={!skipReason.trim()} onClick={handleSkipSubmit}>
              Skip Step
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Retry History Dialog */}
      <Dialog open={showRetryHistory} onOpenChange={setShowRetryHistory}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Retry History: {currentStepResult?.step_name}
            </DialogTitle>
            <DialogDescription>
              {retryHistory.length} previous attempt{retryHistory.length !== 1 ? "s" : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {retryHistory.map((h, i) => (
              <div key={i} className="p-3 rounded-lg border bg-muted/50">
                <div className="flex items-center gap-2 mb-1">
                  {getStatusIcon(h.status)}
                  <span className="font-medium text-sm capitalize">Attempt {h.attempt + 1}: {h.status}</span>
                  {h.ai_elapsed_ms != null && (
                    <span className="text-xs text-muted-foreground">
                      {h.ai_elapsed_ms >= 1000 ? `${(h.ai_elapsed_ms / 1000).toFixed(1)}s` : `${h.ai_elapsed_ms}ms`}
                    </span>
                  )}
                  {h.ai_analysis?.confidence != null && (
                    <Badge variant="outline" className="ml-auto text-xs">{h.ai_analysis.confidence}%</Badge>
                  )}
                </div>
                {h.ai_analysis?.summary && <p className="text-xs text-muted-foreground">{cleanSummary(h.ai_analysis.summary)}</p>}
                {h.notes && <p className="text-xs mt-1">Notes: {h.notes}</p>}
                {h.manual_override && (
                  <p className="text-xs mt-1 text-blue-600">Manual {h.manual_override.action}: {h.manual_override.comments}</p>
                )}
                {h.verified_at && <p className="text-xs text-muted-foreground mt-1">{new Date(h.verified_at).toLocaleString()}</p>}
                {(h.captured_image_url || h.captured_image_base64) && (
                  <div className="relative mt-2 group">
                    <img
                      src={h.captured_image_url || `data:image/jpeg;base64,${h.captured_image_base64}`}
                      alt={`Attempt ${h.attempt + 1}`}
                      className="rounded border w-full max-h-40 object-contain bg-black"
                    />
                    <button
                      onClick={() => setFullscreenImage({ src: h.captured_image_url || `data:image/jpeg;base64,${h.captured_image_base64}`, title: `Attempt ${h.attempt + 1}` })}
                      className="absolute top-1 right-1 p-1 rounded bg-black/50 text-white hover:bg-black/70 transition-colors"
                    >
                      <Maximize2 className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Fullscreen Image Viewer */}
      {fullscreenImage && (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-black/90"
          onClick={() => setFullscreenImage(null)}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3 shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="text-white text-sm font-medium">{fullscreenImage.title}</span>
            <button
              onClick={() => setFullscreenImage(null)}
              className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>
          {/* Image */}
          <div
            className="flex-1 flex items-center justify-center p-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={fullscreenImage.src}
              alt={fullscreenImage.title}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
          {/* Mobile tap-to-close hint */}
          <p className="text-center text-white/40 text-xs pb-4 shrink-0">Tap outside image to close</p>
        </div>
      )}
    </>
  );
};
